<template>
  <section
    v-editable="blok"
    ref="sectionRef"
    class="relative flex flex-col only-child:min-h-[60vh]"
    :class="{
      'first:pt-header-candle': blok.modules[0].component !== 'ModuleFeature',
    }"
  >
    <ElementPattern
      v-if="blok.backgroundPattern"
      :show="inView"
      :bloks="blok.backgroundPattern"
      :fadeOut="true"
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
    />
    <div data-name="section-content">
      <template v-for="module_ in blok.modules" :key="module_._uid">
        <LContentVisibility
          :parallax="module_.component !== 'ModuleFeature'"
          :class="{
            'first:module-neg-mt-half':
              firstSection && module_.component !== 'ModuleFeature',
          }"
        >
          <SbModuleFeature
            v-if="module_.component === 'ModuleFeature'"
            :blok="module_"
            class="relative"
          />
          <SbModuleRichTextMedia
            v-else-if="module_.component === 'ModuleRichTextMedia'"
            :blok="module_"
            class="relative"
          />
          <SbModuleRichText
            v-else-if="module_.component === 'ModuleRichText'"
            :blok="module_"
            class="relative"
          />
          <SbModuleMedia
            v-else-if="module_.component === 'ModuleMedia'"
            :blok="module_"
            class="relative"
          />
          <SbModuleSpotlight
            v-else-if="module_.component === 'ModuleSpotlight'"
            :blok="module_"
            class="relative"
          />
          <SbModuleCards
            v-else-if="module_.component === 'ModuleCards'"
            :blok="module_"
            class="relative"
          />
          <SbModuleFaq
            v-else-if="module_.component === 'ModuleFaq'"
            :blok="module_"
            class="relative"
          />
          <SbModuleBookCarousel
            v-else-if="module_.component === 'ModuleBookCarousel'"
            :blok="module_"
            class="relative"
          />
          <SbModuleTimeline
            v-else-if="module_.component === 'ModuleTimeline'"
            :blok="module_"
            class="relative"
          />
          <SbModuleArticlesLatest
            v-else-if="module_.component === 'ModuleArticlesLatest'"
            :blok="module_"
            class="relative"
          />
          <!-- <SbModuleKlaviyoForm
            v-else-if="module_.component === 'ModuleKlaviyoForm'"
            :blok="module_"
            class="relative"
          /> -->
          <SbModuleMailchimpForm
            v-else-if="module_.component === 'ModuleMailchimpForm'"
            :blok="module_"
            class="relative"
          />
          <SbModuleArticlesMediaGallery
            v-else-if="module_.component === 'ModuleArticlesMediaGallery'"
            :blok="module_"
            class="relative"
          />
          <SbModuleSocialMediaFeed
            v-else-if="module_.component === 'ModuleSocialMediaFeed'"
            :blok="module_"
            class="relative"
          />
          <SbModuleCharacters
            v-else-if="module_.component === 'ModuleCharacters'"
            :blok="module_"
            class="relative"
          />
        </LContentVisibility>
        <SbModuleModal
          v-if="module_.component === 'ModuleModal'"
          :blok="module_"
        />
      </template>
    </div>
  </section>
</template>
<!-- class="outline outline-red" -->
<!-- FOR DEV: outline used for taking screenshots of components -->

<script setup lang="ts">
import useSectionTheme from '@/composables/useSectionTheme';
import { SectionStoryblok } from '@/types';
import { ISbPluginThemeColorPicker } from '@/types';

const SbModuleFeature = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleFeature.vue'),
);
const SbModuleRichTextMedia = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleRichTextMedia.vue'),
);
const SbModuleRichText = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleRichText.vue'),
);
const SbModuleMedia = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleMedia.vue'),
);
const SbModuleSpotlight = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleSpotlight.vue'),
);
const SbModuleCards = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleCards.vue'),
);
const SbModuleFaq = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleFaq.vue'),
);
const SbModuleBookCarousel = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleBookCarousel.vue'),
);
const SbModuleTimeline = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleTimeline.vue'),
);
const SbModuleArticlesLatest = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleArticlesLatest.vue'),
);
// const SbModuleKlaviyoForm = defineAsyncComponent(
//   () => import('@/components/sb-modules/SbModuleKlaviyoForm.vue'),
// );
const SbModuleMailchimpForm = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleMailchimpForm.vue'),
);
const SbModuleArticlesMediaGallery = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleArticlesMediaGallery.vue'),
);
const SbModuleSocialMediaFeed = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleSocialMediaFeed.vue'),
);
const SbModuleCharacters = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleCharacters.vue'),
);
const SbModuleModal = defineAsyncComponent(
  () => import('@/components/sb-modules/SbModuleModal.vue'),
);

interface Props {
  blok: SectionStoryblok;
  firstSection?: boolean;
}
const props = defineProps<Props>();

const sectionRef = ref<HTMLElement | null>(null);
const { observeThemeSection } = useSectionTheme();

const theme = computed(() => {
  const colors = props.blok.backgroundColor as ISbPluginThemeColorPicker;
  return {
    bg: colors.hex,
    text: colors.hexFg,
  };
});

const { inView } = observeThemeSection(sectionRef, theme);
</script>
